// lib/reportService.js
// Content/user reporting — synced with mobile (src/lib/reportService.ts)
// Firestore: reports/{reportId}

import { db } from './firebase';
import { addDoc, collection, serverTimestamp } from 'firebase/firestore';

const MAX_SNAPSHOT = 500;

/**
 * Submit a user/content report. Writes a single doc into reports/.
 * Admin queue at /admin/reports actions within 24h.
 *
 * @param {Object} input
 * @param {'profile'|'chat_message'} input.type
 * @param {'maven'|'seeker'} input.targetType
 * @param {string} input.targetId
 * @param {string} [input.targetName]
 * @param {string} input.reporterId
 * @param {string} [input.reporterName]
 * @param {string} [input.reporterRole]
 * @param {string} [input.reason]
 * @param {{chatId?: string, messageId?: string, messageText?: string}} [input.contentRef]
 * @returns {Promise<string>} the new report id
 */
export async function submitReport(input) {
  if (!input?.reporterId) throw new Error('Missing reporterId');
  if (!input?.targetId) throw new Error('Missing targetId');
  if (input.reporterId === input.targetId) throw new Error('Cannot report yourself');

  const trimmedReason = (input.reason || '').trim().slice(0, 1000);
  const contentRef = input.contentRef
    ? {
        chatId: input.contentRef.chatId || null,
        messageId: input.contentRef.messageId || null,
        messageText: (input.contentRef.messageText || '').slice(0, MAX_SNAPSHOT),
      }
    : null;

  const docRef = await addDoc(collection(db, 'reports'), {
    type: input.type,
    targetType: input.targetType,
    targetId: input.targetId,
    targetName: input.targetName || null,
    reporterId: input.reporterId,
    reporterName: input.reporterName || null,
    reporterRole: input.reporterRole || null,
    reason: trimmedReason || null,
    contentRef,
    status: 'pending',
    platform: 'web',
    createdAt: serverTimestamp(),
  });

  // Best-effort admin email notification — never fail the report if email fails.
  try {
    const base = process.env.NEXT_PUBLIC_API_URL;
    if (base) {
      await fetch(`${base}/api/send-report-notification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reportId: docRef.id,
          type: input.type,
          targetType: input.targetType,
          targetId: input.targetId,
          targetName: input.targetName || null,
          reporterId: input.reporterId,
          reporterName: input.reporterName || null,
          reporterRole: input.reporterRole || null,
          reason: trimmedReason || null,
          messageText: contentRef?.messageText || null,
          platform: 'web',
        }),
      });
    }
  } catch (e) {
    console.error('Report notification email failed:', e);
  }

  return docRef.id;
}
